import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { ADMIN_MFA_REQUIRED_EVENT, api } from './api-client';
import { adminTimeZone, setAdminTimeZone } from './time-zone';

/**
 * 어드민 인증 — access 토큰은 메모리에만, refresh 는 httpOnly 쿠키.
 * (localStorage 제거: 어드민 토큰은 XSS 탈취 시 피해가 가장 큰 자산)
 */
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

interface AdminSession {
  accessToken: string;
  refreshToken?: string;
}

interface AdminAuthValue {
  session: AdminSession | null;
  ready: boolean;
  setSession(s: AdminSession): void;
  signOut(): void;
  /** 로그아웃하고 로그인 화면에 이유를 보여 준다 (2단계 인증을 켠 뒤 등) */
  signOutWithNotice(notice: string): void;
  /** 로그인 화면에 보여 줄 안내 — 다시 로그인하면 지운다 */
  notice: string | null;
}

/** OTP 없이 로그인한 세션이 2단계 인증에 막혔을 때 */
export const MFA_RELOGIN_NOTICE =
  '관리자 2단계 OTP 인증이 켜져 있어요. OTP 코드를 넣어 다시 로그인해 주세요.';

const AdminAuthContext = createContext<AdminAuthValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<AdminSession | null>(null);
  const [ready, setReady] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // 시각 표시 기준 — 로그인한 어드민 계정 시간대. 바뀌면 아래 key 로 화면을 다시 그린다
  const [timeZone, setTimeZone] = useState(adminTimeZone);
  const restored = useRef(false);
  const accessToken = session?.accessToken ?? null;
  const signedIn = accessToken !== null;

  const setSession = useCallback((s: AdminSession) => {
    setNotice(null);
    setSessionState({ accessToken: s.accessToken });
  }, []);

  const signOut = useCallback(() => {
    void api.logout().catch(() => undefined);
    setSessionState(null);
  }, []);

  const signOutWithNotice = useCallback(
    (message: string) => {
      setNotice(message);
      signOut();
    },
    [signOut],
  );

  // 2단계 인증에 막힌 요청(403 ADMIN_MFA_REQUIRED) — 이유 모를 오류 대신 안내와 함께 로그인 화면으로
  useEffect(() => {
    if (!signedIn) return;
    const onMfaRequired = () => signOutWithNotice(MFA_RELOGIN_NOTICE);
    window.addEventListener(ADMIN_MFA_REQUIRED_EVENT, onMfaRequired);
    return () => window.removeEventListener(ADMIN_MFA_REQUIRED_EVENT, onMfaRequired);
  }, [signedIn, signOutWithNotice]);

  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    api
      .refresh()
      .then((t) => setSessionState({ accessToken: t.accessToken }))
      .catch(() => undefined)
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!session) return;
    let last = Date.now();
    const doRefresh = async () => {
      try {
        const next = await api.refresh();
        last = Date.now();
        setSessionState({ accessToken: next.accessToken });
      } catch {
        setSessionState(null);
      }
    };
    const timer = setInterval(doRefresh, REFRESH_INTERVAL_MS);
    // 절전/백그라운드 복귀 시 즉시 갱신 (타이머는 잠든 사이 멈춤)
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - last > 60_000) {
        void doRefresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [session]);

  useEffect(() => {
    if (!signedIn || !accessToken) return;
    let cancelled = false;
    api
      .me(accessToken)
      .then((me) => {
        if (cancelled) return;
        setAdminTimeZone(me.timeZone);
        setTimeZone(me.timeZone);
      })
      .catch(() => undefined); // 실패하면 기본 시간대 그대로
    return () => {
      cancelled = true;
    };
    // 로그인 상태가 바뀔 때만 — 토큰 갱신마다 다시 부르지 않는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  const value = useMemo(
    () => ({ session, ready, setSession, signOut, signOutWithNotice, notice }),
    [session, ready, setSession, signOut, signOutWithNotice, notice],
  );

  return (
    <AdminAuthContext.Provider value={value}>
      <Fragment key={timeZone}>{children}</Fragment>
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth(): AdminAuthValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be inside <AdminAuthProvider>');
  return ctx;
}
